import {
  useWSSnackbar,
  WSActions,
  WSForm,
  WSInfoBox,
  WSInputText,
  WSList,
  WSModal,
  WSModalCustomProps
} from "@wingspanhq/fe-component-library";
import { Payer } from "@wingspanhq/wingspan-api-sdk";
import React from "react";
import * as Yup from "yup";
import { ErrorAlert } from "../../../../v3/components/ErrorAlert";
import { validatorEmail } from "../../../../shared/validators/validatorEmail";
import {
  contactCompanyValidator,
  validatorFirstName,
  validatorLastName
} from "../../../../utils/validators";
import { useCreateClient } from "../../hooks/useCreateClient";

export type AddClientModalProps = WSModalCustomProps<Payer | void> & {
  onSuccess?: (client?: Payer) => Promise<void>;
};

type FormData = {
  company: string;
  firstName: string;
  lastName: string;
  email: string;
};

export const AddClientModal: React.FC<AddClientModalProps> = props => {
  const { openSnackbar } = useWSSnackbar();
  const [createClient, createClientMeta] = useCreateClient();

  return (
    <WSModal title="Add client" size="S" onClose={props.onClose}>
      <WSForm<FormData>
        defaultValues={{ company: "", firstName: "", lastName: "", email: "" }}
        validationSchema={Yup.object().shape(
          {
            company: Yup.string().when("firstName", {
              is: (firstName: string) => firstName?.length > 0,
              then: contactCompanyValidator,
              otherwise: contactCompanyValidator.required(
                "At least one of company or name must be provided"
              )
            }),
            firstName: Yup.string().when("company", {
              is: (company: string) => company?.length > 0,
              then: validatorFirstName,
              otherwise: validatorFirstName.required(
                "At least one of company or name must be provided"
              )
            }),
            lastName: validatorLastName,
            email: validatorEmail.required("Required")
          },
          [["company", "firstName"]]
        )}
        onSubmit={async data => {
          const result = await createClient({
            email: data.email,
            company: data.company,
            firstName: data.firstName,
            lastName: data.lastName
          });

          if (!result) return;

          await props.onSuccess?.(result);
          props.onClose(result);
          openSnackbar({
            duration: 5000,
            type: "success",
            message: "Client added successfully"
          });
        }}
      >
        <WSList gap="L">
          <WSInfoBox>
            After adding a client, they’ll be invited by the email provided to
            link their Wingspan account.
          </WSInfoBox>

          <WSForm.Field
            name="company"
            label="Client company"
            component={WSInputText}
            componentProps={{ placeholder: "Client company" }}
          />
          <WSForm.Field
            name="firstName"
            label="Client first name"
            component={WSInputText}
            componentProps={{ placeholder: "First name" }}
          />
          <WSForm.Field
            name="lastName"
            label="Client last name"
            component={WSInputText}
            componentProps={{ placeholder: "Last name" }}
          />
          <WSForm.Field
            name="email"
            label="Client email *"
            component={WSInputText}
            componentProps={{ placeholder: "Client email" }}
          />

          <ErrorAlert error={createClientMeta.error} context="CreateClient" />

          <WSActions
            mt="XL"
            alignment="fill"
            buttons={[
              {
                label: "Add client",
                kind: "Primary",
                type: "submit",
                loading: createClientMeta.isLoading
              },
              {
                label: "Cancel",
                kind: "Secondary",
                type: "button",
                onClick() {
                  props.onClose();
                }
              }
            ]}
          />
        </WSList>
      </WSForm>
    </WSModal>
  );
};
